import store from '../index';
import {
  addCentralizedError,
  showErrorModal,
  SiteEvaluationError,
} from '../slices/siteEvaluationSlice';
import { generateErrorId, extractOriginalMessage, ensureRetry } from './errorHandlerBase';

/**
 * API name constants for Site Evaluation module
 */
export const SITE_EVALUATION_API_NAMES = {
  FETCH_TABLE: 'fetchSiteEvaluationTable',
  FETCH_RECOMMENDED_ACTION_PLAN: 'fetchRecommendedActionPlan',
  FETCH_ACTIVE_ITEMS: 'fetchActiveItems',
  SUBMIT_ACTION_PLAN: 'submitActionPlan',
  FETCH_EMPLOYEES: 'fetchSiteEvaluationEmployees',
  FETCH_QUESTIONS: 'fetchSiteEvaluationQuestions',
  FETCH_BY_ID: 'fetchSiteEvaluationById',
} as const;

export const getUserFriendlyErrorMessage = (apiName: string, originalMessage?: string): string => {
  const friendly: Record<string, string> = {
    [SITE_EVALUATION_API_NAMES.FETCH_TABLE]: 'Unable to load site evaluations. Please try again.',
    [SITE_EVALUATION_API_NAMES.FETCH_RECOMMENDED_ACTION_PLAN]: 'Unable to load recommended action plan. Please try again.',
    [SITE_EVALUATION_API_NAMES.FETCH_ACTIVE_ITEMS]: 'Unable to load active items. Please try again.',
    [SITE_EVALUATION_API_NAMES.SUBMIT_ACTION_PLAN]: 'Failed to submit action plan. Please try again.',
  };

  return friendly[apiName] || `An error occurred. ${originalMessage || 'Please try again.'}`;
};

export const handleSiteEvaluationErrorWithFriendlyMessage = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
): SiteEvaluationError => {
  const originalMessage = extractOriginalMessage(error);
  const friendlyMessage = getUserFriendlyErrorMessage(apiName, originalMessage);

  const centralizedError: SiteEvaluationError = {
    id: generateErrorId(),
    apiName,
    message: ensureRetry(friendlyMessage),
    timestamp: Date.now(),
    context: {
      originalMessage,
      ...context,
      originalError: error,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
    },
  };

  // Dispatch to Redux store
  store.dispatch(addCentralizedError(centralizedError));

  if (displayModal) {
    store.dispatch(showErrorModal(centralizedError));
  }

  console.error(`[SiteEvaluation Error] ${apiName}:`, {
    message: originalMessage,
    errorId: centralizedError.id,
    originalError: error,
    context: centralizedError.context,
  });

  return centralizedError;
};

export const safeSiteEvaluationApiCall = async <T>(
  apiCall: (config?: any) => Promise<T>,
  apiName: string,
  displayModal: boolean = true,
  context?: any,
  options?: { skipOrgContext?: boolean }
): Promise<T | null> => {
  try {
    // Pass skipOrgContext flag to apiCall if provided
    console.log('[safeSiteEvaluationApiCall] options:', options);
    console.log('[safeSiteEvaluationApiCall] skipOrgContext:', options?.skipOrgContext);
    const config = options?.skipOrgContext ? { _skipOrgContextForQuestions: true } : undefined;
    console.log('[safeSiteEvaluationApiCall] Generated config:', config);
    const result = await apiCall(config);
    return result;
  } catch (err) {
    handleSiteEvaluationErrorWithFriendlyMessage(apiName, err, displayModal, context);
    return null;
  }
};
